/**
 * SvgAsset — renders an inline SVG string from a question row's asset_ref.
 *
 * Used by exercise views when `exercise.asset_type === 'svg'`. The SVG
 * is produced offline by scripts/svg/* renderers and stored verbatim on
 * the row, so the frontend just inlines it.
 *
 * Sanitization: DOMPurify (svg profile) strips anything unexpected. Our
 * renderers emit a tight whitelist (svg, g, path, line, circle, rect,
 * polygon, text, tspan) so this is pass-through for legitimate content
 * and a safety net against any future pipeline drift.
 */
import DOMPurify from 'dompurify';
import { useMemo } from 'react';

interface SvgAssetProps {
  /** Inline SVG markup (from `exercise.asset_ref`). */
  svg: string;
  /** Optional extra classes for sizing/spacing. */
  className?: string;
  /** Accessible label describing what the image shows. */
  ariaLabel?: string;
}

export function SvgAsset({ svg, className, ariaLabel }: SvgAssetProps) {
  const clean = useMemo(
    () =>
      DOMPurify.sanitize(svg, {
        USE_PROFILES: { svg: true, svgFilters: true },
        ADD_TAGS: ['use'],
        ADD_ATTR: ['viewBox'],
      }),
    [svg],
  );

  return (
    <div
      className={`svg-asset ${className ?? ''}`}
      role="img"
      aria-label={ariaLabel ?? 'Bildaufgabe'}
      style={{
        color: 'var(--ink)',        // so currentColor resolves to a visible ink colour
        lineHeight: 0,              // prevent phantom inline-block gap below SVG
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
      dangerouslySetInnerHTML={{ __html: clean }}
    />
  );
}
